import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";

export default function Renewals() {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leases");
      setLeases(data);
    } catch { toast.error("Failed to load leases"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openDraft = async (lease) => {
    setActive(lease);
    setDraft({ subject: lease.draft_subject || "", body: lease.draft_body || "" });
  };

  const generate = async () => {
    if (!active) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/leases/${active.id}/draft-renewal`, { tone: "friendly" });
      setDraft({ subject: data.subject, body: data.body });
      toast.success("Draft ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    setDrafting(false);
  };

  const send = async () => {
    if (!active) return;
    setSending(true);
    try {
      // Ensure latest draft is persisted by regenerating if missing
      if (!active.draft_body && !draft.body) {
        await generate();
      }
      await api.post(`/leases/${active.id}/send-renewal`);
      toast.success(`Renewal sent to ${active.tenant_name}`);
      setActive(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  const daysUntil = (iso) => {
    const d = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
    return d;
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="renewals-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-400">Lease Renewals</div>
        <h1 className="font-heading text-4xl font-semibold text-stone-900 mt-1">Active & upcoming leases</h1>
        <p className="text-stone-500 mt-1">Generate AI renewal offers and send with one click.</p>
      </div>

      {loading && <div className="text-stone-400">Loading…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {leases.map((l) => {
          const days = daysUntil(l.end_date);
          const urgent = days < 30;
          return (
            <Card key={l.id} className="p-5 border-stone-200 shadow-none rounded-xl bg-white hover-lift" data-testid={`lease-card-${l.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg font-medium text-stone-900">{l.tenant_name}</div>
                  <div className="text-sm text-stone-500">{l.tenant_email}</div>
                </div>
                {l.status === "renewal_sent" ? (
                  <Badge className="bg-forest-50 text-forest-600 border border-forest-200">Sent</Badge>
                ) : urgent ? (
                  <Badge className="bg-[#FBEAE3] text-terra-500 border border-terra-300">{days}d left</Badge>
                ) : (
                  <Badge className="bg-sand-200 text-stone-700">{days}d left</Badge>
                )}
              </div>
              <div className="mt-3 text-sm text-stone-600">{l.property_address}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Current rent</div>
                  <div className="font-heading text-lg">${l.current_rent}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Proposed</div>
                  <div className="font-heading text-lg text-forest-600">${l.proposed_rent}</div>
                </div>
              </div>
              <div className="mt-5 flex gap-2">
                <Button
                  onClick={() => openDraft(l)}
                  className="flex-1 bg-forest-600 hover:bg-forest-500 text-white"
                  data-testid={`draft-renewal-${l.id}`}
                >
                  <Sparkle size={16} weight="fill" className="mr-2" /> {l.draft_body ? "Review draft" : "Draft with AI"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="renewal-draft-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Renewal Offer — {active?.tenant_name}
            </DialogTitle>
            <div className="text-sm text-stone-500">{active?.property_address}</div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">Subject</div>
              <div className="border border-stone-200 rounded-md px-3 py-2 bg-sand-50 text-sm">
                {draft.subject || <span className="text-stone-400">Generate draft to see subject</span>}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-1">Body</div>
              <Textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="Click 'Generate with AI' to draft the email."
                rows={12}
                className="font-sans"
                data-testid="renewal-draft-body"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={generate} disabled={drafting} data-testid="renewal-generate-btn">
              <Sparkle size={16} className="mr-2" /> {drafting ? "Generating…" : "Generate with AI"}
            </Button>
            <Button
              onClick={send}
              disabled={sending || !draft.body}
              className="bg-forest-600 hover:bg-forest-500 text-white"
              data-testid="renewal-send-btn"
            >
              <PaperPlaneTilt size={16} className="mr-2" /> {sending ? "Sending…" : "Send renewal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
